// ===========================================
// The Onboarding block on an enquiry (enquiries Phase 6, plan §11a.8, S2)
// ===========================================
// At Onboarding: each linked student's checklist progress, due day and state. With no child linked
// to a student the enquiry never moves on its own, so it says so. The link to the check-in page
// shows only to someone who may read it.

import { Box, Link, Paper, Stack, Typography } from '@mui/material';
import { Link as RouterLink } from 'react-router-dom';
import type { EnquiryOnboardingBlock as Block } from '../../../api/enquiry.api';
import { useTenant } from '../../../contexts/TenantContext';
import { usePermissions } from '../../../hooks/usePermissions';
import SettlingInStateChip from '../settlingIn/SettlingInStateChip';
import { formatDay } from './enquiryUi';

export default function EnquiryOnboardingBlock({ block }: { block: Block | null | undefined }) {
  const { can } = usePermissions();
  const { schoolSlug } = useTenant();
  if (!block) return null;
  const mayOpen = can('student_onboarding', 'read');

  return (
    <Paper variant="outlined" sx={{ p: 2 }} data-testid="enquiry-panel-onboarding">
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: 1, mb: 1 }}>
        <Typography variant="subtitle2" color="text.secondary">
          Onboarding
        </Typography>
        {mayOpen && block.checkIns.length > 0 && (
          <Link component={RouterLink} to={`/${schoolSlug}/admin/settling-in`} variant="body2">
            Open the check-in
          </Link>
        )}
      </Box>
      {block.linkedStudents === 0 ? (
        <Typography variant="body2" color="text.secondary">
          Link the children to track onboarding. Until then, mark the enquiry enrolled when ready.
        </Typography>
      ) : (
        <Stack spacing={1}>
          {block.checkIns.map((c) => (
            <Box key={c.studentId} sx={{ minWidth: 0 }}>
              <Stack direction="row" spacing={1} alignItems="center" flexWrap="wrap" useFlexGap>
                <Typography variant="body2" fontWeight={600} sx={{ overflowWrap: 'anywhere' }}>
                  {c.name}
                </Typography>
                <SettlingInStateChip state={c.state} />
                <Typography variant="body2" color="text.secondary">
                  {c.done}/{c.total}
                </Typography>
              </Stack>
              <Typography variant="body2" color="text.secondary">
                First class: {c.firstClassDate ? formatDay(c.firstClassDate) : 'Not scheduled'} · Due: {c.dueDate ? formatDay(c.dueDate) : '—'}
              </Typography>
            </Box>
          ))}
          {block.withoutCheckIn > 0 && (
            <Typography variant="body2" color="text.secondary" data-testid="onboarding-without-check-in">
              {block.withoutCheckIn === 1
                ? '1 linked student has no check-in: mark enrolled when ready.'
                : `${block.withoutCheckIn} linked students have no check-in: mark enrolled when ready.`}
            </Typography>
          )}
        </Stack>
      )}
    </Paper>
  );
}
